import Utils from '../../utils/Utils';

const EMPTY_TEXTS = {};

const asObjectTexts = (texts) => {
    if (!texts) {
        return EMPTY_TEXTS;
    }

    if (Array.isArray(texts)) {
        return texts.reduce((accumulator, item = {}, index) => {
            const textKey = `text${index + 1}`;
            const colorKey = `color${index + 1}`;

            if (item.text) {
                accumulator[textKey] = item.text;
            }

            if (item.color) {
                accumulator[colorKey] = item.color;
            }

            return accumulator;
        }, {});
    }

    return texts;
};

const normalizeTranslation = (translation = {}) => ({
    ...translation,
    txtPromo: {
        ...(translation.txtPromo || {}),
        texts: asObjectTexts(translation.txtPromo && translation.txtPromo.texts),
    },
    btnSuscription: {
        ...(translation.btnSuscription || {}),
        texts: asObjectTexts(translation.btnSuscription && translation.btnSuscription.texts),
    },
    btnFeatures: {
        ...(translation.btnFeatures || {}),
        texts: asObjectTexts(translation.btnFeatures && translation.btnFeatures.texts),
    },
});

const getResponseOffers = (response) => {
    if (response && Array.isArray(response.data)) {
        return response.data;
    }

    if (response && Array.isArray(response.offers)) {
        return response.offers;
    }

    console.warn('[PlanSelector][OffersV4] Malformed offers response', response);
    return [];
};

const getPrice = (price) => {
    if (price && typeof price === 'object' && price.amount !== undefined) {
        return String(price.amount);
    }

    if (price === undefined || price === null) {
        return '';
    }

    return String(price);
};

const getCurrency = (price, currency) => {
    if (price && typeof price === 'object' && price.currency !== undefined) {
        return String(price.currency);
    }

    return currency || '';
};

const getFrequency = (offer, translation) => {
    return offer.frequency || offer.periodicity || translation.labelTemporality || '';
};

const getLabelTaxes = (offer, translation) => {
    if (offer.labelTaxes !== undefined) {
        return offer.labelTaxes;
    }

    if (translation.labelTaxes !== undefined) {
        return translation.labelTaxes;
    }

    return null;
};

const getOfferFamily = (offer) => {
  const family = offer.family || offer.producttype || '';
    return family.toLowerCase();
};

const getPromotionTexts = (offer) => {
    return (offer.translation && offer.translation.txtPromo && offer.translation.txtPromo.texts) || EMPTY_TEXTS;
};

const getSubscriptionTexts = (offer) => {
    return (offer.translation && offer.translation.btnSuscription && offer.translation.btnSuscription.texts) || EMPTY_TEXTS;
};

const getFeaturesTexts = (offer) => {
    return (offer.translation && offer.translation.btnFeatures && offer.translation.btnFeatures.texts) || EMPTY_TEXTS;
};

const buildPriceLabel = (offer) => {
    return [offer.currency, offer.price, offer.frequency].filter(Boolean).join(' ').trim();
};

const buildLandingButton = (offer) => {
    const promotionTexts = getPromotionTexts(offer);

    return {
        ...offer,
        provider: offer.producttype,
        purchasable: offer.purchasable,
        prefix: '',
        additionalInfo: false,
        offerTitle: false,
        landingPrice: {
            key: '',
            label: buildPriceLabel(offer),
        },
        landingTax: {
            key: '',
            label: offer.labelTaxes || '',
        },
        landingBonus: {
            key: '',
            label: promotionTexts.text1 || '',
        },
        landingBonusComplement: {
            key: '',
            label: [promotionTexts.text2, promotionTexts.text3].filter(Boolean).join(' '),
        },
    };
};

export const deriveOfferIdFromCheckoutLink = (href = '') => {
    if (!href) {
        return '';
    }

    const matches = href.match(/[?&]offer_id=([^&#]+)/);
    return matches && matches[1] ? decodeURIComponent(matches[1]) : '';
};

export const normalizeOffersV4Response = (response) => {
    return getResponseOffers(response).map((offer) => {
        const translation = normalizeTranslation(offer.translation);
        const checkoutHref = offer && offer._links && offer._links.checkout && offer._links.checkout.href || '';
        const productId = offer.product_id || deriveOfferIdFromCheckoutLink(checkoutHref);

        return {
            ...offer,
            product_id: productId,
            producttype: offer.producttype || '',
            family: getOfferFamily(offer),
            purchasable: Object.prototype.hasOwnProperty.call(offer, 'purchasable') ? offer.purchasable : true,
            price: getPrice(offer.price),
            currency: getCurrency(offer.price, offer.currency),
            frequency: getFrequency(offer, translation),
            labelTaxes: getLabelTaxes(offer, translation),
            request: Utils.getRequest(checkoutHref),
            linkworkflowstart: checkoutHref,
            href: checkoutHref,
            assets: {
                background: {
                    aaf: offer && offer.assets && offer.assets.background && offer.assets.background.aaf || '',
                },
                logo: {
                    aaf: offer && offer.assets && offer.assets.logo && offer.assets.logo.aaf || '',
                },
            },
            translation,
            _links: offer && offer._links ? offer._links : {},
        };
    });
};

export const parsePlanOffersV4 = (offer, handlePlanClick) => ({
    ...offer,
    id: offer.product_id,
    provider: offer.producttype,
    family: getOfferFamily(offer),
    offerid: offer.product_id,
    providerLogo: offer.assets.logo.aaf,
    isButtonVisible: offer.purchasable,
    onPlanClick: handlePlanClick && typeof handlePlanClick === 'function' ? () => handlePlanClick({...offer, banner: `banner_${getOfferFamily(offer)}`}) : null,
});

export const parsePlansOffersV4 = (offers, handlePlanClick) => {
    return offers.map((offer) => parsePlanOffersV4(offer, handlePlanClick));
};

export const buildPlanSelectorOffersV4Details = (offers) => {
    return offers.map((offer) => {
        const family = getOfferFamily(offer);

        return {
            id: offer.product_id,
            providerLogo: offer.assets.logo.aaf,
            pbi: {...offer, banner: `banner_${family}`.toLowerCase()},
            buttons: offers
                .filter((candidate) => getOfferFamily(candidate) === family)
                .map((candidate) => buildLandingButton(candidate)),
        };
    });
};

export const mapV4OfferToCardData = (offer, onPlanClick) => {
    const promotionTexts = getPromotionTexts(offer);
    const subscriptionTexts = getSubscriptionTexts(offer);
    const featuresTexts = getFeaturesTexts(offer);

    return {
        imagenFondo_aaf: offer.assets.background.aaf,
        imagenLogo_aaf: offer.assets.logo.aaf,
        TextoTemporalidad: offer.translation.labelTemporality || '',
        TextImpuesto: offer.labelTaxes === null ? '' : offer.labelTaxes || '',
        TextoPromocion1: promotionTexts.text1 || '',
        TextoPromocion2: promotionTexts.text2 || '',
        TextoPromocion3: promotionTexts.text3 || '',
        TextoBotonSubscripcion1: subscriptionTexts.text1 || '',
        TextoBotonSubscripcion2: subscriptionTexts.text2 || '',
        TextoBotonIncluye3: featuresTexts.text1 || '',
        buttonTextColorHex: offer.translation.btnSuscription && offer.translation.btnSuscription.texts.text1 || '#FFF',
        buttonBGColorHex: offer.translation.btnSuscription && offer.translation.btnSuscription.bgColor || '#000',
        id: offer.product_id,
        offerid: offer.product_id,
        isButtonVisible: offer.purchasable,
        price: offer.price,
        currency: offer.currency,
        productType: offer.producttype,
        family: getOfferFamily(offer),
        onPlanClick: onPlanClick || null,
    };
};



// WEBPACK FOOTER //
// ./src/payments/PlanSelector/offersV4Adapter.js